import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Circle, Eye, FileText, Mail, Network, Pencil, Play, Share2, Trash2, ExternalLink } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Btn, Card, Confirm, Empty, FilePickButton, IconBtn, InfoRow, Loading, MediaViewer, Modal, Page, Section, Select, cx } from "../components/wf";
import {
  api, budgetLevel, budgetTone, computeTotals, copyLink, currentMonth, emailResultMessage, fileUrl, isVideo, money, monthLabel, num, openLink, progressOf, shiftMonth, uploadFile,
} from "../lib/api";
import { useAuth, usePerms } from "../lib/auth";
import { useRefreshJobs, useStatuses } from "../lib/queries";
import { PunchList } from "../components/PunchList";

const QUARTER = ["None", "¼", "½", "¾", "Completed"];
export const SECTIONS = [
  { kind: "work", title: "Work To Be Completed" },
  { kind: "parts", title: "Parts Used" },
  { kind: "hours", title: "Hours Log" },
  { kind: "expenses", title: "Daily Expenses" },
  { kind: "daily_notes", title: "Daily Notes" },
  { kind: "hardware", title: "Extra Hardware Used" },
  { kind: "notes", title: "Monthly Notes" },
];
const EMAIL_KINDS = {
  kastle: { label: "Kastle PM", status: "email kastle", log: "kastle_emails", kind: "email kastle" },
  invoice: { label: "Invoice summary", status: "to be invoiced", log: "invoice_emails", kind: "to be invoiced" },
  approval: { label: "Approval email", status: "email for approval", log: "approval_emails", kind: "email for approval" },
  monthly: { label: "Monthly report", status: "send monthly report", log: "monthly_emails", kind: "send monthly report" },
};

function Total({ label, value, testid, danger }) {
  return (
    <div className="rounded-xl border border-wf-line bg-wf-s2 px-4 py-3">
      <p className="truncate text-[11px] font-semibold text-wf-mute">{label}</p>
      <p data-testid={testid} className={cx("mt-1 truncate text-lg font-extrabold", danger && "text-wf-err")}>{value}</p>
    </div>
  );
}

function MonthBar({ value, onChange, testid = "wo-month" }) {
  const m = value ?? currentMonth();
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2" data-testid={testid}>
      <button data-testid={`${testid}-all`} onClick={() => onChange(null)} className={cx("rounded-full px-3 py-1.5 text-xs font-bold", !value ? "bg-wf-ink text-white" : "bg-wf-s3 text-wf-ink2")}>All time</button>
      <div className="flex items-center gap-1 rounded-full bg-wf-s3 px-1">
        <button data-testid={`${testid}-prev`} onClick={() => onChange(shiftMonth(m, -1))} className="rounded-full p-1.5 text-wf-brand hover:bg-wf-soft"><ChevronLeft size={16} /></button>
        <button data-testid={`${testid}-label`} onClick={() => onChange(m)} className={cx("min-w-[120px] text-xs font-bold", value && "text-wf-brand")}>{monthLabel(m)}</button>
        <button data-testid={`${testid}-next`} disabled={m >= currentMonth()} onClick={() => onChange(shiftMonth(m, 1))} className="rounded-full p-1.5 text-wf-brand hover:bg-wf-soft disabled:opacity-30"><ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

function BudgetAlert({ pct, remaining }) {
  if (pct == null) return null;
  const level = budgetLevel(pct);
  const tone = budgetTone(pct);
  const color = tone === "over" ? "var(--wf-err)" : tone === "warn" ? "var(--wf-warn)" : "var(--wf-ok)";
  return (
    <div data-testid="budget-alert" className="mt-3 rounded-xl border bg-wf-s2 p-4" style={{ borderColor: color }}>
      <p className="flex items-center gap-1.5 text-sm font-bold" style={{ color }} data-testid="budget-alert-text">
        {level && <AlertTriangle size={16} />}
        {level === "over" ? `Over budget by ${money(-remaining)}` : level === "warn" ? `Budget warning · ${Math.round(pct)}% used` : `${Math.round(pct)}% of budget used`}
      </p>
      <div className="mt-2 h-2 overflow-hidden rounded bg-wf-s3"><div className="h-2 rounded" style={{ width: `${Math.min(pct, 100)}%`, background: color }} /></div>
      {level === "warn" && <p className="mt-2 text-sm text-wf-ink2">{money(remaining)} left before this job goes over budget.</p>}
    </div>
  );
}

function entryView(kind, e) {
  if (kind === "work") return { title: e.work || "Work item", lines: [e.date, e.description, e.parts_used && `Parts: ${e.parts_used} × ${num(e.quantity)}`, e.helper_names?.length && `Helpers: ${e.helper_names.join(", ")}`] };
  if (kind === "parts")
    return {
      title: e.parts_used || "Part",
      lines: [[e.part_number && `#${e.part_number}`, e.supplier].filter(Boolean).join(" · "), `${e.date} · Qty ${num(e.quantity)}${e.unit_cost ? ` × ${money(e.unit_cost)}` : ""}`, e.description],
      amount: e.unit_cost ? money(e.quantity * e.unit_cost) : "",
    };
  if (kind === "hours")
    return {
      title: `${e.date} · ${num(e.hours)} hrs × ${e.num_techs} techs`,
      lines: [e.tech_names.join(", "), `${num(e.hours * e.num_techs)} man hours`,
        e.per_diem_cost ? `${e.num_per_diems} per diem${e.num_per_diems === 1 ? "" : "s"} × ${money(e.per_diem_rate ?? 0)} = ${money(e.per_diem_cost)}` : ""],
      amount: e.labor_cost != null ? money(e.labor_cost) : "",
    };
  if (kind === "expenses") return { title: e.description || "Expense", lines: [e.date, e.details], amount: e.cost != null ? money(e.cost) : "" };
  if (kind === "hardware") return { title: e.description || "Hardware", lines: [e.date, `Quantity: ${num(e.quantity)}`] };
  return { title: e.date || "Note", lines: [e.note] };
}

function EntryCard({ kind, e, canEdit, onEdit, onDelete, onProgress }) {
  const v = entryView(kind, e);
  return (
    <div data-testid={`entry-${kind}-${e.id}`} className="mb-2 flex items-start gap-3 rounded-xl border border-wf-line bg-wf-s2 p-4">
      <div className="min-w-0 flex-1">
        <p className="font-bold">{v.title}</p>
        {v.lines.filter(Boolean).map((l, i) => <p key={i} className="whitespace-pre-wrap text-[13px] text-wf-ink2">{l}</p>)}
        <p className="mt-1 text-[11px] text-wf-mute">{[e.created_by && `By ${e.created_by}`, e.updated_by && `edited by ${e.updated_by}`, e.from_tasks && "Includes parts from project items — change them on the project"].filter(Boolean).join(" · ")}</p>
        {onProgress && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[0.25, 0.5, 0.75, 1].map((q) => {
              const on = progressOf(e) >= q;
              const cur = progressOf(e) === q;
              return (
                <button key={q} data-testid={q === 1 ? `complete-entry-${e.id}` : `progress-entry-${e.id}-${q * 4}`} onClick={() => onProgress(cur ? q - 0.25 : q)}
                  className={cx("flex h-8 items-center gap-1 rounded-full border-[1.5px] border-green-500 px-3 text-xs font-bold transition-colors", on ? "bg-green-500 text-white" : "text-green-600 hover:bg-green-50")}>
                  {q === 1 && (on ? <CheckCircle2 size={14} /> : <Circle size={14} />)}{QUARTER[q * 4]}
                </button>
              );
            })}
          </div>
        )}
        {!!e.completed && !!e.completed_at && <p className="mt-1 text-[11px] text-wf-mute">Completed {new Date(e.completed_at).toLocaleDateString()}{e.completed_by ? ` by ${e.completed_by}` : ""}</p>}
      </div>
      {v.amount && <p className="font-extrabold text-wf-brand">{v.amount}</p>}
      {canEdit && (
        <div className="flex">
          <IconBtn icon={Pencil} onClick={onEdit} data-testid={`edit-entry-${e.id}`} />
          <IconBtn icon={Trash2} tone="danger" onClick={onDelete} data-testid={`delete-entry-${e.id}`} />
        </div>
      )}
    </div>
  );
}

function KastleIncludeDialog({ open, onClose, onSend }) {
  const [inc, setInc] = useState([]);
  useEffect(() => {
    if (open) setInc([]);
  }, [open]);
  return (
    <Modal open={open} onClose={onClose} title="Email Kastle" testid="kastle-include-sheet">
      <p className="text-sm text-wf-mute">Parts used and the job photo link are always included. Also include:</p>
      {["hours", "notes", "expenses"].map((k) => (
        <label key={k} className="flex cursor-pointer items-center gap-3 border-b border-wf-line py-3 font-semibold capitalize">
          <input type="checkbox" data-testid={`kastle-include-${k}`} checked={inc.includes(k)} onChange={() => setInc((v) => (v.includes(k) ? v.filter((x) => x !== k) : [...v, k]))} className="h-5 w-5 accent-[var(--wf-brand)]" />
          {k}
        </label>
      ))}
      <div className="mt-2 flex justify-end gap-2">
        <Btn variant="ghost" onClick={onClose} data-testid="kastle-include-cancel">Cancel</Btn>
        <Btn icon={Mail} onClick={() => onSend(inc)} data-testid="kastle-include-send">Send Email</Btn>
      </div>
    </Modal>
  );
}

function EmailBox({ k, wo, busy, onSend, onPreview, reportMonth }) {
  const cfg = EMAIL_KINDS[k];
  const log = wo[cfg.log];
  const last = log?.[log.length - 1];
  return (
    <div data-testid={`${k}-email-box`} className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-wf-soft p-3">
      <p className="min-w-[200px] flex-1 text-[13px] text-wf-softink">
        {last ? `${cfg.label}${last.month ? ` (${monthLabel(last.month)})` : ""} last emailed to ${last.to} on ${new Date(last.sent_at).toLocaleString()}` : `${cfg.label} hasn't been emailed yet.`}
        {k === "monthly" && ` · Report month: ${monthLabel(reportMonth)}`}
      </p>
      {onPreview && <Btn size="sm" variant="ghost" icon={Eye} onClick={onPreview} data-testid={`preview-${k}-email`}>Preview</Btn>}
      <Btn size="sm" variant="ghost" icon={Mail} loading={busy} onClick={onSend} data-testid={`email-${k}-button`}>{last ? "Resend" : "Send"}</Btn>
    </div>
  );
}

export default function WorkOrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const refresh = useRefreshJobs();
  const { user } = useAuth();
  const { isManager, seeMoney } = usePerms();
  const statuses = useStatuses();
  const q = useQuery({ queryKey: ["work-order", id], queryFn: () => api(`/work-orders/${id}`) });
  const wo = q.data;
  const [month, setMonth] = useState(null);
  const [reportMonth, setReportMonth] = useState(currentMonth());
  const [confirm, setConfirm] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [kastleAsk, setKastleAsk] = useState(null);
  const [emailsOpen, setEmailsOpen] = useState(false);
  const [emailing, setEmailing] = useState(null);
  const [busyPdf, setBusyPdf] = useState(false);

  useEffect(() => {
    if (q.data?.was_new) refresh();
  }, [q.data?.was_new]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (patch, kastleInclude) => {
    const cur = qc.getQueryData(["work-order", id]) ?? wo;
    const body = {
      customer_id: cur.customer_id, job_description: cur.job_description, description_details: cur.description_details, hours_availability: cur.hours_availability,
      status: cur.status, job_notes: cur.job_notes, job_photos: cur.job_photos, drive_link: cur.drive_link, budget: cur.budget, assigned_to: cur.assigned_to ?? [], ...patch,
    };
    try {
      const res = await api(`/work-orders/${id}${kastleInclude ? `?kastle_include=${kastleInclude}` : ""}`, { method: "PUT", body });
      refresh(res);
      return res;
    } catch (e) {
      toast.error(e.message);
    }
  };
  const showEmail = (r) => (r.ok ? toast.success(emailResultMessage(r)) : toast.error(emailResultMessage(r)));

  const setStatus = async (name, include) => {
    if (name.trim().toLowerCase() === "email kastle" && !include && name !== wo.status) return setKastleAsk({ status: name });
    const res = await update({ status: name }, include?.join(","));
    if (res?.email_result) showEmail(res.email_result);
    else if (res) toast.success(`Status: ${name}`);
  };
  const sendEmail = async (k, include) => {
    if (k === "kastle" && !include) return setKastleAsk({});
    setEmailing(k);
    try {
      const qs = k === "monthly" ? `?month=${reportMonth}` : k === "kastle" ? `?include=${include.join(",")}` : "";
      const r = await api(`/work-orders/${id}/email-${k}${qs}`, { method: "POST" });
      showEmail({ ...r, kind: EMAIL_KINDS[k].kind });
      q.refetch();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setEmailing(null);
    }
  };
  const preview = async (k) => {
    try {
      const { url } = await api(`/work-orders/${id}/email-preview/${k}${k === "monthly" ? `?month=${reportMonth}` : ""}`, { method: "POST" });
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e.message || "Could not open the preview");
    }
  };
  const setProgress = async (e, progress) => {
    try {
      refresh(await api(`/work-orders/${id}/entries/work/${e.id}/complete`, { method: "POST", body: { progress } }));
      toast.success(progress >= 1 ? "Marked completed" : progress === 0 ? "Marked not started" : `Marked ${QUARTER[progress * 4]} complete`);
    } catch (err) {
      toast.error(err.message);
    }
  };
  const onConfirm = async () => {
    try {
      if (confirm === "order") {
        await api(`/work-orders/${id}`, { method: "DELETE" });
        refresh();
        toast.success("Work order deleted");
        nav("/");
      } else refresh(await api(`/work-orders/${id}/entries/${confirm.kind}/${confirm.id}`, { method: "DELETE" }));
    } catch (e) {
      toast.error(e.message);
    }
  };
  const addMedia = async (files, field = "job_photos") => {
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadFile(f));
      const cur = qc.getQueryData(["work-order", id])?.[field] ?? [];
      await update(field === "floor_plans" ? { floor_plans: [...cur, ...paths], floor_plans_available: true } : { job_photos: [...cur, ...paths] });
      toast.success(paths.length === 1 ? "File added" : `${paths.length} files added`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  if (q.isLoading) return <Loading />;
  if (!wo) return <Page title="Job" back><Empty title="Could not load order details" subtitle={q.error?.message} /></Page>;

  const c = wo.customer;
  const t = computeTotals(wo, month);
  const st = wo.status.trim().toLowerCase();
  const logs = Object.values(EMAIL_KINDS).map((k) => wo[k.log]);
  const hasEmails = Object.values(EMAIL_KINDS).some((k) => k.status === st) || logs.some((l) => !!l?.length) || !!wo.approval_decisions?.length;
  const emailCount = logs.reduce((n, l) => n + (l?.length ?? 0), 0);
  const canEdit = (e) => isManager || (!!e.created_by_id && e.created_by_id === user?.id);
  const lastDecision = wo.approval_decisions?.[wo.approval_decisions.length - 1];
  const entryUrl = (kind, eid) => `/work-orders/${id}/entry/${kind}${eid ? `/${eid}` : ""}`;

  return (
    <Page title={c?.name ?? "Job"} subtitle={wo.job_description} back testid="work-order-detail"
      right={<>
        {seeMoney && <IconBtn icon={FileText} disabled={busyPdf} data-testid="share-pdf-button" title="PDF report"
          onClick={async () => { setBusyPdf(true); try { await openLink(`/work-orders/${id}/report-link`); } catch (e) { toast.error(e.message); } finally { setBusyPdf(false); } }} />}
        <IconBtn icon={Pencil} data-testid="work-order-edit-button" onClick={() => nav(`/work-orders/${id}/edit`)} title="Edit" />
        {isManager && <IconBtn icon={Trash2} tone="danger" data-testid="work-order-delete-button" onClick={() => setConfirm("order")} title="Delete" />}
      </>}>
      <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
        <div className="min-w-0 lg:order-2">
          <MonthBar value={month} onChange={setMonth} />
          <div className="rounded-2xl bg-wf-brand p-5 text-wf-onbrand" data-testid="work-order-totals">
            <p className="text-sm font-semibold opacity-85">{month ? `Man Hours · ${monthLabel(month)}` : "Total Man Hours"}</p>
            <p data-testid="wo-man-hours" className="text-5xl font-extrabold tracking-tight">{num(t.man_hours)}</p>
            <p data-testid="wo-total-hours" className="text-sm opacity-85">{num(t.total_hours)} total hours</p>
          </div>
          {seeMoney && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Total label="Man-hour Cost" value={money(t.labor_cost)} testid="wo-labor-cost" />
              <Total label="Total Expenses" value={money(t.total_expenses)} testid="wo-total-expenses" />
              <Total label="Total Per Diems" value={money(t.total_per_diems)} testid="wo-total-per-diems" />
              <Total label="Working Budget" value={money(wo.totals.working_budget ?? wo.budget)} testid="wo-working-budget" />
              <Total label={month ? "Month Total" : "Project Total"} value={money(t.total_cost)} testid="wo-total-cost" />
              {wo.billing_type !== "hourly" && <Total label="Remaining" value={money(wo.totals.budget_remaining)} testid="wo-budget-remaining" danger={!!wo.budget && wo.totals.budget_remaining < 0} />}
            </div>
          )}
          {seeMoney && wo.billing_type === "hourly" && (
            <div className="mt-2 rounded-xl border border-wf-brand bg-wf-s2 p-4" data-testid="wo-billable">
              <p className="text-xs font-bold text-wf-mute">Billable Total (hourly)</p>
              <p className="text-2xl font-extrabold" data-testid="wo-billable-total">{money(t.billable_total)}</p>
              <p className="text-sm text-wf-ink2">{num(t.man_hours)} man hours × {money(wo.hourly_rate ?? 0)}</p>
            </div>
          )}
          {seeMoney && wo.billing_type !== "hourly" && <BudgetAlert pct={wo.totals.budget_used_pct} remaining={wo.totals.budget_remaining} />}
          {(!!wo.project_id || isManager) && (
            <Btn variant="secondary" icon={Network} className="mt-3 w-full" data-testid="wo-project-button"
              onClick={async () => {
                try {
                  const pid = wo.project_id ?? (await api(`/work-orders/${id}/project`, { method: "POST" })).id;
                  if (!wo.project_id) refresh();
                  nav(`/projects/${pid}`);
                } catch (e) { toast.error(e.message); }
              }}>{wo.project_id ? "Open Project" : "Add Project"}</Btn>
          )}

          <Section title="Status" />
          <Select testid="detail-status-dropdown" value={wo.status} options={(statuses.data ?? []).map((s) => s.name)} onChange={(v) => v && setStatus(v)} className="mb-0" />
          {hasEmails && (
            <button data-testid="emails-toggle" onClick={() => setEmailsOpen((o) => !o)} className="mt-3 flex min-h-[48px] w-full items-center gap-2 rounded-xl border border-wf-line bg-wf-s2 px-4 text-left font-semibold">
              <Mail size={17} className="text-wf-brand" /><span className="flex-1">Emails{emailCount ? ` · ${emailCount} sent` : ""}</span>
              <ChevronDown size={17} className={cx("text-wf-mute transition-transform", emailsOpen && "rotate-180")} />
            </button>
          )}
          {emailsOpen && Object.entries(EMAIL_KINDS).map(([k, cfg]) => (st === cfg.status || !!wo[cfg.log]?.length) && (
            <React.Fragment key={k}>
              {k === "monthly" && (
                <div className="mt-2 flex items-center gap-2 rounded-xl bg-wf-s2 px-3 py-2 text-sm" data-testid="monthly-report-month">
                  <span className="flex-1 font-semibold text-wf-mute">Report month</span>
                  <button data-testid="monthly-month-prev" onClick={() => setReportMonth((m) => shiftMonth(m, -1))} className="text-wf-brand"><ChevronLeft size={18} /></button>
                  <span className="font-bold" data-testid="monthly-month-label">{monthLabel(reportMonth)}</span>
                  <button data-testid="monthly-month-next" disabled={reportMonth >= currentMonth()} onClick={() => setReportMonth((m) => shiftMonth(m, 1))} className="text-wf-brand disabled:opacity-30"><ChevronRight size={18} /></button>
                </div>
              )}
              <EmailBox k={k} wo={wo} reportMonth={reportMonth} busy={emailing === k} onSend={() => sendEmail(k)} onPreview={seeMoney ? () => preview(k) : undefined} />
            </React.Fragment>
          ))}
          {emailsOpen && lastDecision && (
            <p className="mt-2 rounded-xl bg-wf-soft p-3 text-[13px] text-wf-softink" data-testid="approval-decision">
              {lastDecision.decision === "approved" ? "Approved" : "Rejected"} by email on {new Date(lastDecision.at).toLocaleString()}{lastDecision.reason ? ` · "${lastDecision.reason}"` : ""}
            </p>
          )}

          <Section title="Customer" />
          <Card testid="customer-info-card" className="py-2">
            <InfoRow label="Address" value={[c?.address, c?.city, c?.state].filter(Boolean).join(", ")} />
            <InfoRow label="Building / Floor(s)" value={c?.building_floors} />
            <InfoRow label="Phone" value={c?.phone} />
            <InfoRow label="Contact" value={[c?.contact, c?.contact_phone, c?.contact_email].filter(Boolean).join(" · ")} />
            <InfoRow label="Kastle Customer #" value={c?.kastle_customer_number} testid="wo-kastle-number" />
            {!!wo.kastle_contact?.name && <InfoRow label="Kastle PM" testid="wo-kastle-pm" value={[wo.kastle_contact.name, wo.kastle_contact.phone, wo.kastle_contact.email, wo.kastle_contact.location].filter(Boolean).join(" · ")} />}
          </Card>
        </div>

        <div className="min-w-0 lg:order-1">
          <Section title="Job Details" />
          <Card testid="job-details-card" className="py-2">
            <InfoRow label="Job Description" value={wo.job_description} />
            <InfoRow label="Description Details" value={wo.description_details} />
            <InfoRow label="Hours of Availability" value={wo.hours_availability} />
            <InfoRow label="Job Notes" value={wo.job_notes} />
            <InfoRow label="Assigned To" value={(wo.assigned_users ?? []).map((u) => u.name).join(", ")} testid="wo-assigned-users" />
            {!wo.job_description && !wo.description_details && !wo.job_notes && <p className="py-3 text-sm text-wf-mute">No job details yet. Click edit to add.</p>}
          </Card>

          {(wo.floor_plans_available || !!wo.floor_plans?.length) && (
            <>
              <Section title={`Floor Plans (${wo.floor_plans?.length ?? 0})`}>
                <FilePickButton testid="add-floor-plan-button" variant="ghost" accept="image/*" multiple loading={uploading} onFiles={(f) => addMedia(f, "floor_plans")}>Add</FilePickButton>
              </Section>
              <div className="flex flex-wrap gap-2">
                {(wo.floor_plans ?? []).map((p, i) => (
                  <button key={p} onClick={() => nav(`/work-orders/${id}/floor-plan?path=${encodeURIComponent(p)}`)} data-testid={`floor-plan-${i}`} className="relative" title="Open to add or edit pins">
                    <img src={fileUrl(p)} alt="" className="h-28 w-28 rounded-xl bg-wf-s3 object-cover" />
                    {!!(wo.floor_plan_pins ?? []).filter((x) => x.path === p).length && (
                      <span className="absolute right-1 top-1 rounded-full bg-wf-brand px-1.5 text-[11px] font-bold text-wf-onbrand">{(wo.floor_plan_pins ?? []).filter((x) => x.path === p).length} pins</span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}

          <Section title={`Job Photos (${wo.job_photos.length})`}>
            <div className="flex items-center gap-1">
              {wo.job_photos.length > 0 && (
                <Btn size="sm" variant="ghost" icon={Share2} data-testid="share-job-photos-button"
                  onClick={() => copyLink(`/work-orders/${id}/share-link`).then(() => toast.success("Photos link copied")).catch((e) => toast.error(e.message))}>Share link</Btn>
              )}
              <FilePickButton testid="add-job-photo-button" variant="ghost" size="sm" accept="image/*,video/*" multiple loading={uploading} onFiles={addMedia}>Add</FilePickButton>
            </div>
          </Section>
          <div className="flex flex-wrap gap-2">
            {wo.job_photos.map((p, i) => (
              <button key={p} data-testid={`job-photo-${i}`} onClick={() => setViewer({ path: p, field: "job_photos" })}>
                {isVideo(p) ? (
                  <div className="flex h-24 w-24 flex-col items-center justify-center rounded-xl bg-wf-inverse text-white"><Play size={28} /><span className="text-xs font-bold">Video</span></div>
                ) : <img src={fileUrl(p)} alt="" loading="lazy" className="h-24 w-24 rounded-xl bg-wf-s3 object-cover transition-transform hover:scale-[1.03]" />}
              </button>
            ))}
            {!wo.job_photos.length && <p className="text-sm text-wf-mute">No photos yet.</p>}
          </div>
          {!!wo.drive_link && (
            <a href={wo.drive_link} target="_blank" rel="noreferrer" data-testid="open-drive-link" className="mt-3 inline-flex items-center gap-2 font-semibold text-wf-brand hover:underline">
              Open Google Drive folder <ExternalLink size={15} />
            </a>
          )}

          <PunchList wid={id} items={wo.entries.punch ?? []} canEdit={canEdit} onUpdated={refresh} onDelete={(eid) => setConfirm({ kind: "punch", id: eid })} />

          {SECTIONS.map((s) => (
            <div key={s.kind} data-testid={`entries-section-${s.kind}`}>
              <Section title={`${s.title} (${wo.entries[s.kind]?.length ?? 0})`} actionLabel="Add" onAction={() => nav(entryUrl(s.kind))} testid={`add-entry-${s.kind}`} />
              {(wo.entries[s.kind] ?? []).length === 0 ? (
                <button onClick={() => nav(entryUrl(s.kind))} className="w-full rounded-xl border border-dashed border-wf-line p-4 text-left text-sm text-wf-mute hover:border-wf-brand2">No entries yet</button>
              ) : [...wo.entries[s.kind]].reverse().map((e) => (
                <EntryCard key={e.id} kind={s.kind} e={e} canEdit={canEdit(e) && !e.from_tasks} onEdit={() => nav(entryUrl(s.kind, e.id))} onDelete={() => setConfirm({ kind: s.kind, id: e.id })}
                  onProgress={s.kind === "work" ? (v) => setProgress(e, v) : undefined} />
              ))}
            </div>
          ))}
        </div>
      </div>

      <MediaViewer path={viewer?.path} list={viewer ? wo[viewer.field] : undefined} onChange={(p) => setViewer((v) => ({ ...v, path: p }))} onClose={() => setViewer(null)}
        onRemove={async () => { const v = viewer; setViewer(null); await update({ [v.field]: wo[v.field].filter((x) => x !== v.path) }); }} />
      <KastleIncludeDialog open={!!kastleAsk} onClose={() => setKastleAsk(null)}
        onSend={(inc) => { const a = kastleAsk; setKastleAsk(null); if (a?.status) setStatus(a.status, inc); else sendEmail("kastle", inc); }} />
      <Confirm open={!!confirm} title={confirm === "order" ? "Delete this work order?" : "Delete this entry?"} onConfirm={onConfirm} onClose={() => setConfirm(null)} />
    </Page>
  );
}
